import React from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import {bindActionCreators, Dispatch} from "redux";
import {connect} from "react-redux";
import {
    KatButtonGroup,
    KatButton,
    KatLabel,
    KatBox,
    KatHmd,
    KatLink,
    KatAlert,
    KatTabs,
    KatTab,
} from "@amzn/katal-react";
import ExistingProductSearchComponent from "./ExistingProductSearchComponent";
import MetricsUtil from "src/utils/MetricsUtil";
import NewAsinEstimationComponent from "./NewAsinEstimationComponent";
import BulkSearchComponent from "../bulk/BulkSearchComponent";
import {
    getHMDId,
    getLocaleConfig,
    isLoginMode,
    isSurchargeMarketplaceMap,
    surchargeBannerStringIDMap,
    surchargeDateStringIDMap,
    surchargeRateMap,
    surchargeHelpPageLinkStringIDMap,
    surchargeHelpPageLinkMap,
    helpPageUrl
} from "src/config/Config";
import i18next from "i18next";
import HMD from "katal-sellercentralhmdsupport";
import {removeQueryProductInfo} from "src/actions/QueryProductUpdateAction";
import ApplicationState from "src/model/ApplicationState";
import {GetCurrentCountryCodeResponse, GetIsSelectNew, GetProductDataResponse} from "src/model/ProductMatch";
import RequestStatus from "src/model/RequestStatus";
import {removeSelectedProduct} from "src/actions/SelectedProductQueryAction";
import {setSelectNew} from "src/actions/IsSelectNew";
import {removeRecommendation} from "src/actions/RecommendationQueryAction";
import {GetWeblabTreatmentResponse} from "src/model/WeblabTreatment";
import FormatUtil from "src/utils/FormatUtil";

interface StateProps {
    currentCountryCodeRes: GetCurrentCountryCodeResponse,
    isSelectNewRes: GetIsSelectNew,
    productRes: GetProductDataResponse,
    weblabTreatmentsRes: GetWeblabTreatmentResponse
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    currentCountryCodeRes: state.currentCountryCodeRes,
    isSelectNewRes: state.isSelectNewRes,
    productRes: state.productRes,
    weblabTreatmentsRes: state.weblabTreatmentsRes
});

interface DispatchProps {
    readonly dispatchRemoveSelectedProduct: () => void,
    readonly dispatchRemoveProduct: () => void,
    readonly dispatchRemoveRecommendation: () => void,
    readonly dispatchSetSelectNew: (selectNew: boolean) => void
}

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchRemoveSelectedProduct: removeSelectedProduct,
        dispatchRemoveProduct: removeQueryProductInfo,
        dispatchRemoveRecommendation: removeRecommendation,
        dispatchSetSelectNew: setSelectNew,
    }, dispatch)
);

const connector = connect(mapStateToProps, mapDispatchToProps);

interface OwnProps {
    readonly metricsUtil?: MetricsUtil
}

type Props = StateProps & OwnProps & DispatchProps & WithTranslation;

interface State {
    hmdOpen: boolean,
    hmdState: KatHmd.State,
    countryCode: string,
    tabsTriggerStatus: Map<string, boolean>
}

const REVCAL_HMD_ID: string = "RevenueCalculator_HMD_ID";

export class ProductSearchComponent extends React.Component<Props, State> {

    private readonly metricsUtil: MetricsUtil;
    private hmd: any = undefined;

    constructor(props: Props) {
        super(props);

        const defaultPageIndex = this.props.isSelectNewRes.selectNew ? "2" : "1";
        this.state = {
            hmdOpen: false,
            hmdState: "normal",
            countryCode: "",
            tabsTriggerStatus: new Map<string, boolean>([[defaultPageIndex, true]])
        };

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }

        this.handleButtonChange = this.handleButtonChange.bind(this);
        this.handleTabChange = this.handleTabChange.bind(this);
        this.handleCountryChange = this.handleCountryChange.bind(this);
    }

    hasProducts(): boolean {
        return (this.props.productRes && this.props.productRes.requestStatus === RequestStatus.SUCCESS);
    }

    getCurrentCountryCode() {
        if (this.state.countryCode != '') {
            return this.state.countryCode;
        }
        if (this.props.currentCountryCodeRes.requestStatus === RequestStatus.SUCCESS) {
            return this.props.currentCountryCodeRes.currentCountryCode;
        }
        return "";
    }

    handleCountryChange(countryCode: string) {
        this.setState({countryCode: countryCode});
    }

    handleButtonChange(event: KatButtonGroup.ChangeEvent) {
        if (this.hasProducts() && this.state.countryCode != this.props.productRes.productData.data.countryCode) {
            this.setState({countryCode: this.props.productRes.productData.data.countryCode});
        }
        this.props.dispatchSetSelectNew(event.detail.value === "new");
        this.props.dispatchRemoveSelectedProduct();
        this.props.dispatchRemoveProduct();
        this.props.dispatchRemoveRecommendation();
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(event.detail.value, "click");
        }
    }

    handleTabChange(event: KatTabs.ChangeEvent) {
        if (event.detail.selected == null) {
            return;
        }

        this.state.tabsTriggerStatus.set(event.detail.selected, true);
        if (this.hasProducts() && this.state.countryCode != this.props.productRes.productData.data.countryCode) {
            this.setState({countryCode: this.props.productRes.productData.data.countryCode});
        }

        this.props.dispatchSetSelectNew(event.detail.selected === "2");
        this.props.dispatchRemoveSelectedProduct();
        this.props.dispatchRemoveProduct();
        this.props.dispatchRemoveRecommendation();
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(event.detail.selected, "click");
        }
    }

    private handleHmdClick = () => {
        this.setState({hmdOpen: true});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("HMD", "open");
        }
    }

    private handleHmdDismiss = () => {
        this.setState({hmdOpen: false, hmdState: "normal"});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("HMD", "close");
        }
    }

    private getWeblabTreatment = (name: string): string => {
        let weblab = this.props.weblabTreatmentsRes.weblabTreatments.find(weblab => weblab.weblabId === name);
        return weblab ? weblab.treatment : 'C';
    };

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (isLoginMode() && document.querySelector(`#${REVCAL_HMD_ID}`)) {
            const hmdId = getHMDId();
            this.hmd && this.hmd.unregister();
            this.hmd = new HMD.Builder()
                .withSelector(`#${REVCAL_HMD_ID}`)
                .withSurveyId(hmdId)
                .withCustomAttribute('RevenueCalculator')
                .build()
        }
    }

    produceHMDContent() {
        const {t} = this.props;
        return (
            <>
                <KatLink id="hmdLink" label={t('SC_FBA_Calc_link_feedback')} href='#' variant="link" onClick={(event) => {
                    event.preventDefault();
                    this.handleHmdClick();
                }}/>
                {this.state.hmdOpen &&
                <KatHmd
                    id={REVCAL_HMD_ID}
                    locale={i18next.language}
                    mode="modal"
                    visible={this.state.hmdOpen}
                    state={this.state.hmdState}
                    onClose={this.handleHmdDismiss}/>}
            </>
        );
    }

    produceSurchargeBannerContent(countryCode: string) {
        const {t} = this.props;
        if (isSurchargeMarketplaceMap[countryCode]) {
            const minimumFractionDigits = countryCode == 'US' ? 0 : 1;
            return (
                <KatAlert variant="info" className={"new-version-banner-surcharge"}>
                    <div>
                        {countryCode == 'CA'
                            ? <KatLabel text={t(surchargeBannerStringIDMap[countryCode])}/> :
                            <KatLabel text={t(surchargeBannerStringIDMap[countryCode], {
                                X: t(surchargeDateStringIDMap[countryCode]),
                                Y: FormatUtil.formatPercentage(surchargeRateMap[countryCode], minimumFractionDigits)
                            })}/>
                        }
                        <KatLink label={t(surchargeHelpPageLinkStringIDMap[countryCode])} href={surchargeHelpPageLinkMap[countryCode]}
                                 variant="link"/>
                    </div>
                </KatAlert>
            );
        }
    }

    render() {
        const {t, currentCountryCodeRes, isSelectNewRes} = this.props;
        const localeParam = RequestStatus.SUCCESS === currentCountryCodeRes.requestStatus ? '&lang=' + getLocaleConfig(currentCountryCodeRes.currentCountryCode) : '';
        const legacyUrl = '/fba/profitabilitycalculator/index?ref=RC2' + localeParam;
        const localHelpPageUrl = helpPageUrl + '?ref_=revecalc&language=' + i18next.language;
        const currentCountryCode = this.getCurrentCountryCode();

        return (
            <>
                {this.getWeblabTreatment(isLoginMode() ? 'REV_CAL_1_DEPRECATE_LOGIN_507134' : 'REV_CAL_1_DEPRECATE_NONLOGIN_507136') === 'C' &&
                    <KatAlert variant="info" className={isLoginMode() ? "new-version-banner" : "new-version-banner-nonlogin"}>
                        <div>
                            <KatLabel text={isLoginMode() ? t('SC_FBA_Calc_banner_newVersion') : t('SC_FBA_Calc_banner_newPublicVersion')}/>
                            <KatLabel text={t('SC_FBA_CALC_Banner_Help')}/><KatLink label={t('SC_FBA_CALC_Banner_Help_Page_Link')}
                                                                                    href={localHelpPageUrl} variant="link"/>
                            <KatLink label={t('SC_FBA_Calc_link_goOldVersion')} href={legacyUrl} variant="link" className="old-version-link"/>
                        </div>
                        {isLoginMode() && <div>{this.produceHMDContent()}</div>}
                    </KatAlert>
                }
                {this.getWeblabTreatment(isLoginMode() ? 'REV_CAL_1_DEPRECATE_LOGIN_507134' : 'REV_CAL_1_DEPRECATE_NONLOGIN_507136') === 'T1' &&
                    <KatAlert variant="info" className="new-version-banner-nonlogin">
                        <div>
                            <KatLabel text={t('SC_FBA_CALC_Banner_Help')}/><KatLink label={t('SC_FBA_CALC_Banner_Help_Page_Link')}
                                                                                    href={localHelpPageUrl} variant="link"/>
                            {isLoginMode() && <span className="old-version-link">{this.produceHMDContent()}</span>}
                        </div>
                    </KatAlert>
                }
                {isLoginMode() && this.produceSurchargeBannerContent(currentCountryCode)}

                <KatBox id="ProductSearch" variant="white" className="product-search-box-bulk">
                    <p className="product-search-label-info"> {t('SC_FBA_Calc_title_search')} </p>
                    <KatTabs className="product-search-tabs" selected={isSelectNewRes.selectNew ? "2" : "1"}
                             onChange={this.handleTabChange}>
                        <KatTab label={t('SC_FBA_Calc_tab_search')} tabId="1">
                            {
                                this.checkTabTriggerState("1") &&
                                <ExistingProductSearchComponent currentCountryCode={currentCountryCode}
                                                                metricsUtil={this.metricsUtil}
                                                                handleCountryChange={this.handleCountryChange}/>
                            }
                        </KatTab>
                        <KatTab label={t('SC_FBA_Calc_tab_define')} tabId="2">
                            {
                                this.checkTabTriggerState("2") &&
                                <NewAsinEstimationComponent currentCountryCode={currentCountryCode} metricsUtil={this.metricsUtil}
                                                            handleCountryChange={this.handleCountryChange}/>
                            }
                        </KatTab>
                        <KatTab label={t("SC_FBA_Calc_tab_estimate")} tabId="3">
                            {
                                this.checkTabTriggerState("3") &&
                                <BulkSearchComponent currentCountryCode={currentCountryCode} metricsUtil={this.metricsUtil}
                                                     handleCountryChange={this.handleCountryChange}/>
                            }
                        </KatTab>
                    </KatTabs>
                </KatBox>
            </>
        );
    }

    checkTabTriggerState(tabIndex: string) :boolean {
        const triggerState = this.state.tabsTriggerStatus.get(tabIndex);
        if (triggerState == undefined) {
            return false;
        }
        return triggerState;
    }
}

export default withTranslation()(connector(ProductSearchComponent));